import { Button } from '@remote-com/norma/core/button';
import { RichText } from 'prismic-reactjs';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import CircleSmallImage from './assets/circle-small.svg';

import { transformProperties } from '@/src/domains/prismic/helpers';
import {
  tabletBreakpoint,
  mobileBreakpoint,
  fromTabletBreakpoint,
} from '@/src/domains/shared/breakpoints';

export const Section = styled.section`
  ${fluidProp('padding-bottom', '80px', '152px')}
  position: relative;
  z-index: 1;
`;

export const ComparisonTableWrapper = styled.div`
  ${fluidProp('padding-bottom', '50px', '80px')}

  ${fromTabletBreakpoint(css`
    margin-top: -102px;
  `)}
`;

export const SecondComparisonTableWrapper = styled(ComparisonTableWrapper)`
  ${fromTabletBreakpoint(css`
    display: none;
  `)}
`;

export const TableRowItem = styled.div`
  display: table-cell;
  vertical-align: middle;
  text-align: center;
  border-bottom: 1px solid #d4d7df;
  height: 113px;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }

  ${tabletBreakpoint(css`
    border-right: 1px solid #d4d7df;
    border-left: 1px solid #d4d7df;
    height: auto;
  `)}
`;

export const TableRowHeading = styled.h3`
  text-align: left;
`;

export const ImageWrapper = styled.div`
  position: absolute;
  top: -40%;
  right: 3%;
`;

export const TableHeading = styled.div`
  display: table-cell;
  vertical-align: middle;
  text-align: center;
  ${({ theme }) => theme.typography.titleSM}
  border-bottom: 1px solid #d4d7df;
  height: 101px;
  position: relative;

  &:nth-child(2) {
    background-color: var(--colors-turquoise);
    color: var(--colors-irisBlue);
    border-color: #7764e8;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    text-transform: uppercase;
    ${({ theme }) => theme.typography.titleSM}
  }

  &:nth-child(3) {
    ${tabletBreakpoint(css`
      display: none;
    `)}
  }

  ${tabletBreakpoint(css`
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
    justify-content: center;
  `)}
`;

export const ThirdColumnTitle = styled(TableHeading)`
  color: var(--colors-blank);
  ${({ theme }) => theme.typography.titleSM}
  text-align: center;
  text-transform: uppercase;
  padding: 0 20px;
  margin-bottom: 0;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const ThirdColumnSubtitle = styled.div`
  color: var(--colors-blank);
  ${({ theme }) => theme.typography.bodySM}
  font-weight: 300;
  color: var(--colors-turquoise);
  text-transform: none;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const ThirdTableHeading = styled(TableHeading)`
  && {
    &:nth-child(2) {
      background-color: transparent;
      border-right: 1px solid #d4d7df;
      border-left: 1px solid #d4d7df;
      box-sizing: border-box;
    }
  }
`;

export const TableRow = styled.div`
  display: table-row;
  ${({ theme }) => theme.typography.body}
  color: var(--colors-bayoux);

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }

  > strong {
    font-weight: 600;
  }

  &:last-child {
    ${TableRowItem} {
      height: 46px;
      border: none;

      &:nth-child(2) {
        border-bottom-left-radius: 10px;
        border-bottom-right-radius: 10px;
      }

      ${tabletBreakpoint(css`
        display: none;

        &:nth-child(2) {
          display: block;
          min-height: 20px;
        }
      `)}
    }
  }

  ${TableRowItem} {
    width: 33%;
    min-height: 113px;
    padding: 20px 26px;
    box-sizing: border-box;

    ${tabletBreakpoint(css`
      display: flex;
      flex-direction: column;
      width: 100%;
      align-items: center;
      justify-content: center;
    `)}

    &:nth-child(2) {
      background-color: var(--colors-irisBlue);
      color: var(--colors-moonRaker);
      border-color: #7764e8;
    }

    &:nth-child(3) {
      ${tabletBreakpoint(css`
        display: none;
      `)}
    }
  }
`;

export const ThirdTableRow = styled(TableRow)`
  ${TableRowItem} {
    &:nth-child(2) {
      background-color: none;
      background-image: var(--colors-zircon);
      color: var(--colors-bayoux);
      border-color: #d4d7df;
    }
  }

  &:last-child {
    ${TableRowItem} {
      ${tabletBreakpoint(css`
        &:nth-child(2) {
          display: none;
        }
      `)}
    }
  }
`;

export const Table = styled.div`
  width: 100%;
  display: table;

  ${tabletBreakpoint(css`
    width: 80%;
    display: flex;
    flex-direction: column;
    text-align: center;
    margin: 0 auto;
  `)}

  ${mobileBreakpoint(css`
    width: 100%;
  `)}


  &:first-child {
    ${fromTabletBreakpoint(css`
      ${TableRow} > ${TableHeading} {
        border: none;
      }
    `)}
  }
`;

export const ComparisonBottom = styled.div`
  text-align: center;
`;

export const ComparisonNote = styled.div`
  ${({ theme }) => theme.typography.bodyCaption}
  color: var(--colors-lynch);
  padding: 0 26px;
  margin-bottom: 76px;
  text-align: left;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export function ComparisonTableSlice({ slice }) {
  const { primary } = transformProperties(slice);
  const { secondColumnTitle, thirdColumnTitle, thirdColumnSubtitle, note, ctaLabel, ctaLink } =
    primary;

  const { fields: sections } = slice;

  return (
    <Section>
      <ContentContainer>
        <ComparisonTableWrapper>
          <Table>
            <TableRow>
              <TableHeading />

              <TableHeading>
                <RichText render={secondColumnTitle} />
                <ImageWrapper>
                  <CircleSmallImage />
                </ImageWrapper>
              </TableHeading>
              <ThirdColumnTitle>
                <RichText render={thirdColumnTitle} />
                <ThirdColumnSubtitle>
                  <RichText render={thirdColumnSubtitle} />
                </ThirdColumnSubtitle>
              </ThirdColumnTitle>
            </TableRow>
            {sections.map(
              (
                {
                  first_description: firstDescription,
                  second_description: secondDescription,
                  third_description: thirdDescription,
                },
                index
              ) => (
                <TableRow key={index}>
                  <TableRowItem>
                    {firstDescription && (
                      <TableRowHeading>{RichText.asText(firstDescription)}</TableRowHeading>
                    )}
                  </TableRowItem>

                  <TableRowItem>
                    {secondDescription && <RichText render={secondDescription} />}
                  </TableRowItem>
                  <TableRowItem>
                    {thirdDescription && <RichText render={thirdDescription} />}
                  </TableRowItem>
                </TableRow>
              )
            )}
          </Table>
        </ComparisonTableWrapper>

        {/* third column for small screens */}

        <SecondComparisonTableWrapper>
          <Table>
            <TableRow>
              <TableHeading />
              <ThirdTableHeading>
                <RichText render={thirdColumnTitle} />
              </ThirdTableHeading>
            </TableRow>
            {sections.map(
              (
                { first_description: firstDescription, third_description: thirdDescription },
                index
              ) => (
                <ThirdTableRow key={index}>
                  <TableRowItem>
                    <TableRowHeading>{RichText.asText(firstDescription)}</TableRowHeading>
                  </TableRowItem>

                  <TableRowItem>
                    {thirdDescription && <RichText render={thirdDescription} />}
                  </TableRowItem>
                </ThirdTableRow>
              )
            )}
          </Table>
        </SecondComparisonTableWrapper>
        <ComparisonBottom>
          <ComparisonNote>
            <RichText render={note} />
          </ComparisonNote>
          <Button href={ctaLink.url}>{ctaLabel}</Button>
        </ComparisonBottom>
      </ContentContainer>
    </Section>
  );
}
